import { useAuth } from "@clerk/clerk-react";
import { Plan } from "@/types/api";
import { useUserPlanQuery } from "./queries/usePlansQueries";

export interface SubscriptionInfo {
  planName: string;
  price: number;
  period: "month" | "year";
  isLoaded: boolean;
  isLoading: boolean;
  hasActivePlan: boolean;
  plan?: Plan | null; // Full plan object from API (supports both Clerk and Stripe)
  provider?: "clerk" | "stripe";
}

/**
 * Optimized subscription hook using React Query
 * Replaces manual state management with cached queries
 */
export const useSubscription = (): SubscriptionInfo => {
  const { isLoaded, isSignedIn } = useAuth();
  const { data: activePlan, isLoading, error } = useUserPlanQuery();

  // Return optimized subscription info using React Query data
  if (!isLoaded) {
    return {
      planName: "",
      price: 0,
      period: "month",
      isLoaded: false,
      isLoading: false,
      hasActivePlan: false,
      plan: null,
      provider: undefined,
    };
  }

  if (!isSignedIn) {
    return {
      planName: "",
      price: 0,
      period: "month",
      isLoaded: true,
      isLoading: false,
      hasActivePlan: false,
      plan: null,
      provider: undefined,
    };
  }

  if (isLoading) {
    return {
      planName: "",
      price: 0,
      period: "month",
      isLoaded: true,
      isLoading: true,
      hasActivePlan: false,
      plan: null,
      provider: undefined,
    };
  }

  if (error || !activePlan) {
    return {
      planName: "",
      price: 0,
      period: "month",
      isLoaded: true,
      isLoading: false,
      hasActivePlan: false,
      plan: null,
      provider: undefined,
    };
  }

  // Transform interval from API format to expected format
  const period = activePlan.interval === "monthly" ? "month" : "year";

  return {
    planName: activePlan.name,
    price: activePlan.pricing.amount, // Amount is already in dollars from new API
    period,
    isLoaded: true,
    isLoading: false,
    hasActivePlan: true,
    plan: activePlan,
    provider: activePlan.provider,
  };
};
